/* eslint-disable react/jsx-props-no-spreading */
import ProgressTracker, { type ProgressTrackerItem } from '3-components/ProgressTracker/ProgressTracker';
import * as ReactDOMClient from 'react-dom/client';
import DonationForm from './DonationForm/DonationForm';
import DetailsForm from './DetailsForm';
import PaymentForm from './PaymentForm/PaymentForm';
import DonationBlockProvider, { useDonationBlockProps, type DonationBlockProps } from './DonationBlockProvider';
import { useDonationNavigation } from './libs/useDonationNavigation';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useDonationStore } from './libs/useDonationStore';
import { cn } from 'utils/className';
import ButtonBack from './components/ButtonBack';

const DonationBlock = (): JSX.Element => {
    const { step } = useDonationNavigation();
    const { donationData } = useDonationStore();

    const { labels, backButton } = useDonationBlockProps();

    const isBackButtonEnabled = backButton?.enable;

    const containerClass = 'donation-block-container';
    const stepContainerClass = step ? `is-step-${step}` : '';
    const paymentContainerClass = donationData.payment ? `is-payment-${donationData.payment}` : '';

    const donationSteps: ProgressTrackerItem[] = [
        {
            stepNumber: 1,
            stepNumberLabel: labels?.stepOneNumber ?? '1',
            label: labels?.stepOneLabel ?? 'Amount',
        },
        {
            stepNumber: 2,
            stepNumberLabel: labels?.stepTwoNumber ?? '2',
            label: labels?.stepTwoLabel ?? 'Details',
        },
        {
            stepNumber: 3,
            stepNumberLabel: labels?.stepThreeNumber ?? '3',
            label: labels?.stepThreeLabel ?? 'Payment',
        }
    ];

    return (
        <div className={cn(containerClass, stepContainerClass, paymentContainerClass)}>
            <div className="donation-block-tracker">
                {isBackButtonEnabled && (
                    <div className="donation-block-buttonBack-wrapper">
                        {step > 1 && (
                            <ButtonBack>
                                {backButton.buttonLabel}
                            </ButtonBack>
                        )}
                    </div>
                )}
                <ProgressTracker
                    steps={donationSteps}
                    activeStep={step}
                />
            </div>
            <div className="donation-block-content-wrapper">
                {step === 1 && <DonationForm />}
                {step === 2 && <DetailsForm />}
                {step === 3 && <PaymentForm />}
            </div>
            <div className="donation-block-footer">
                {/** donation footer goes here */}
            </div>
        </div>
    );
};

const queryClient = new QueryClient();

interface DonationBlockInitProps {
    el: HTMLDivElement;
    props: DonationBlockProps;
}

const DonationBlockInit = ({ el, props }: DonationBlockInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <DonationBlockProvider {...props}>
                <DonationBlock />
            </DonationBlockProvider>
        </QueryClientProvider>
    );
};

export default DonationBlockInit;
